import * as React from 'react';
import type { DisplayError, ErrorAction } from '../types/index.js';

/**
 * Toast notification configuration
 */
export interface ToastConfig {
  /** Auto-dismiss timeout in milliseconds (0 = no auto-dismiss) */
  autoHideDuration?: number;
  /** Position of the toast */
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
  /** Maximum number of toasts to show */
  maxToasts?: number;
  /** Animation duration in milliseconds */
  animationDuration?: number;
}

/**
 * Toast item interface
 */
export interface ToastItem {
  id: string;
  displayError: DisplayError;
  timestamp: number;
  dismissed?: boolean;
}

/**
 * Toast context for managing toast state
 */
export interface ToastContextValue {
  toasts: ToastItem[];
  showToast: (displayError: DisplayError) => string;
  hideToast: (id: string) => void;
  clearAllToasts: () => void;
  config: ToastConfig;
  updateConfig: (config: Partial<ToastConfig>) => void;
}

/**
 * Default toast configuration
 */
const defaultToastConfig: Required<ToastConfig> = {
  autoHideDuration: 5000,
  position: 'top-right',
  maxToasts: 5,
  animationDuration: 300
};

/**
 * Toast context
 */
const ToastContext = React.createContext<ToastContextValue | null>(null);

/**
 * Hook to use toast functionality
 */
export function useToast(): ToastContextValue {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

/**
 * Toast provider component
 */
export interface ToastProviderProps {
  children: React.ReactNode;
  config?: Partial<ToastConfig>;
}

export function ToastProvider({ children, config }: ToastProviderProps): JSX.Element {
  const [toasts, setToasts] = React.useState<ToastItem[]>([]);
  const [toastConfig, setToastConfig] = React.useState<ToastConfig>({
    ...defaultToastConfig,
    ...config
  });

  const showToast = React.useCallback((displayError: DisplayError): string => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const toast: ToastItem = {
      id,
      displayError,
      timestamp: Date.now()
    };

    setToasts(prevToasts => {
      const newToasts = [...prevToasts, toast];
      
      // Limit number of toasts
      if (newToasts.length > toastConfig.maxToasts!) {
        return newToasts.slice(-toastConfig.maxToasts!);
      }
      
      return newToasts;
    });

    // Auto-hide if configured
    if (toastConfig.autoHideDuration! > 0) {
      setTimeout(() => {
        hideToast(id);
      }, toastConfig.autoHideDuration);
    }

    return id;
  }, [toastConfig.maxToasts, toastConfig.autoHideDuration]);

  const hideToast = React.useCallback((id: string): void => {
    setToasts(prevToasts => 
      prevToasts.map(toast => 
        toast.id === id ? { ...toast, dismissed: true } : toast
      )
    );

    // Remove from DOM after animation
    setTimeout(() => {
      setToasts(prevToasts => prevToasts.filter(toast => toast.id !== id));
    }, toastConfig.animationDuration);
  }, [toastConfig.animationDuration]);

  const clearAllToasts = React.useCallback((): void => {
    setToasts([]);
  }, []);

  const updateConfig = React.useCallback((newConfig: Partial<ToastConfig>): void => {
    setToastConfig(prev => ({ ...prev, ...newConfig }));
  }, []);

  const contextValue: ToastContextValue = {
    toasts,
    showToast,
    hideToast,
    clearAllToasts,
    config: toastConfig,
    updateConfig
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  );
}

/**
 * Toast container component
 */
function ToastContainer(): JSX.Element {
  const { toasts, config } = useToast();

  const positionClasses = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'top-center': 'top-4 left-1/2 transform -translate-x-1/2',
    'bottom-center': 'bottom-4 left-1/2 transform -translate-x-1/2'
  };

  return (
    <div
      className={`fixed z-50 pointer-events-none ${positionClasses[config.position!]}`}
      style={{ maxWidth: '400px', width: '100%' }}
    >
      <div className="space-y-2">
        {toasts.map((toast) => (
          <ErrorToastItem
            key={toast.id}
            toast={toast}
            animationDuration={config.animationDuration!}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Individual toast item component
 */
interface ErrorToastItemProps {
  toast: ToastItem;
  animationDuration: number;
}

function ErrorToastItem({ toast, animationDuration }: ErrorToastItemProps): JSX.Element {
  const { hideToast } = useToast();
  const { displayError, dismissed } = toast;

  const handleAction = (action: ErrorAction) => {
    action.handler();
    hideToast(toast.id);
  };

  const handleDismiss = () => {
    hideToast(toast.id);
  };

  const typeStyles = {
    error: 'bg-error/10 border-error/20 text-error',
    warning: 'bg-warning/10 border-warning/20 text-warning',
    info: 'bg-info/10 border-info/20 text-info'
  };

  const iconElements = {
    error: (
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    warning: (
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
      </svg>
    ),
    info: (
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )
  };

  return (
    <div
      className={`
        pointer-events-auto transform transition-all duration-${animationDuration}
        ${dismissed 
          ? 'opacity-0 translate-x-full scale-95' 
          : 'opacity-100 translate-x-0 scale-100'
        }
      `}
      style={{
        transitionDuration: `${animationDuration}ms`
      }}
    >
      <div
        className={`
          relative flex items-start gap-3 p-4 rounded-lg border shadow-lg backdrop-blur-sm
          ${typeStyles[displayError.type]}
        `}
        role="alert"
        aria-live="polite"
      >
        {/* Icon */}
        <div className="mt-0.5">
          {iconElements[displayError.type]}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Title */}
          <div className="font-medium text-sm mb-1">
            {displayError.title}
          </div>
          
          {/* Message */}
          <div className="text-sm opacity-90 mb-3">
            {displayError.message}
          </div>

          {/* Field Errors */}
          {Object.keys(displayError.fieldErrors).length > 0 && (
            <div className="mb-3 space-y-1">
              {Object.entries(displayError.fieldErrors).map(([field, error]) => (
                <div key={field} className="text-xs opacity-80">
                  <span className="font-medium">{field}:</span> {error}
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          {displayError.actions && displayError.actions.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {displayError.actions.map((action, index) => (
                <button
                  key={index}
                  onClick={() => handleAction(action)}
                  className={`
                    px-3 py-1 text-xs rounded font-medium transition-colors
                    ${action.type === 'primary' 
                      ? 'bg-current text-base-100 hover:opacity-80' 
                      : action.type === 'danger'
                      ? 'bg-error text-error-content hover:bg-error/80'
                      : 'bg-current/10 hover:bg-current/20'
                    }
                  `}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Dismiss Button */}
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 p-1 hover:bg-current/10 rounded transition-colors"
          aria-label="Dismiss notification"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}

/**
 * Simple error toast function for direct usage
 */
export function showErrorToast(displayError: DisplayError): void {
  // This is a fallback for when ToastProvider is not available
  // In production, you would want to ensure ToastProvider is always available
  console.warn('Error Toast (fallback):', displayError);
  
  // You could implement a simple fallback notification here
  // or throw an error to ensure proper setup
}

/**
 * Hook for showing HTTP error toasts
 */
export function useErrorToast() {
  const toast = useToast();

  const showHttpError = React.useCallback((displayError: DisplayError) => {
    return toast.showToast(displayError);
  }, [toast]);

  const showSimpleError = React.useCallback((message: string, title = 'Error') => {
    const displayError: DisplayError = {
      title,
      message,
      type: 'error',
      retryable: false,
      fieldErrors: {}
    };
    return toast.showToast(displayError);
  }, [toast]);

  const showSuccess = React.useCallback((message: string, title = 'Success') => {
    const displayError: DisplayError = {
      title,
      message,
      type: 'info',
      retryable: false,
      fieldErrors: {}
    };
    return toast.showToast(displayError);
  }, [toast]);

  const showWarning = React.useCallback((message: string, title = 'Warning') => {
    const displayError: DisplayError = {
      title,
      message,
      type: 'warning',
      retryable: false,
      fieldErrors: {}
    };
    return toast.showToast(displayError);
  }, [toast]);

  return {
    showHttpError,
    showSimpleError,
    showSuccess,
    showWarning,
    hideToast: toast.hideToast,
    clearAll: toast.clearAllToasts
  };
}